import { useMemo, useState } from 'react';
import {
  Copy, Check, ExternalLink, GraduationCap, Briefcase, ChevronDown, ChevronUp,
  Mail, MapPin, Linkedin, FileText, Clock, Award, Building2, Calendar,
} from 'lucide-react';
import type { Application, Candidate, EducationEntry } from '@core/types';

interface ExperienceEntry {
  company: string;
  title: string;
  start: string;
  end: string | null;
  bullets: string[];
}

function readExperience(candidate: Candidate): ExperienceEntry[] {
  const json = candidate.profile_structured_json;
  if (!json) {
    if (candidate.current_title || candidate.current_company) {
      return [{
        company: candidate.current_company ?? '—',
        title: candidate.current_title ?? '—',
        start: '',
        end: null,
        bullets: [],
      }];
    }
    return [];
  }
  const entries =
    (json['experience_entries'] as unknown[]) ??
    (json['work_experience'] as unknown[]) ??
    (json['experiences'] as unknown[]) ??
    [];
  if (!Array.isArray(entries) || entries.length === 0) {
    if (candidate.current_title || candidate.current_company) {
      return [{
        company: candidate.current_company ?? '—',
        title: candidate.current_title ?? '—',
        start: '',
        end: null,
        bullets: [],
      }];
    }
    return [];
  }
  return entries.slice(0, 10).map((e) => {
    const obj = e as Record<string, unknown>;
    const bullets: string[] = [];
    const raw = obj['bullets'] ?? obj['responsibilities'] ?? obj['description'] ?? [];
    if (Array.isArray(raw)) bullets.push(...(raw as string[]).filter(Boolean));
    else if (typeof raw === 'string' && raw.trim()) bullets.push(raw.trim());
    return {
      company: String(obj['company'] ?? obj['employer'] ?? '—'),
      title: String(obj['title'] ?? obj['role'] ?? obj['position'] ?? '—'),
      start: String(obj['start'] ?? obj['start_date'] ?? obj['from'] ?? ''),
      end: obj['end'] ? String(obj['end']) : null,
      bullets,
    };
  });
}

function initials(name: string) {
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (!p.length) return '?';
  if (p.length === 1) return p[0]![0]!.toUpperCase();
  return (p[0]![0]! + p[p.length - 1]![0]!).toUpperCase();
}

function ExperienceCard({ entry }: { entry: ExperienceEntry }) {
  const [expanded, setExpanded] = useState(false);
  const visibleBullets = expanded ? entry.bullets : entry.bullets.slice(0, 4);
  return (
    <div className="flex gap-3">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-secondary)] text-[10px] font-semibold vscode-text-secondary">
        {initials(entry.company)}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[14px] font-semibold vscode-text-primary leading-snug">{entry.title}</p>
        <p className="text-[12px] vscode-text-secondary">
          {entry.company}
          {entry.start ? (
            <span className="ml-2 text-[11px] vscode-text-tertiary">
              {entry.start}{entry.end ? ` – ${entry.end}` : ' – Present'}
            </span>
          ) : null}
        </p>
        {visibleBullets.length > 0 ? (
          <ul className="mt-1.5 space-y-0.5">
            {visibleBullets.map((b, i) => (
              <li key={i} className="flex gap-1.5 text-[12px] vscode-text-secondary leading-snug">
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-current opacity-50" aria-hidden />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        ) : null}
        {entry.bullets.length > 4 ? (
          <button
            type="button"
            className="mt-1 flex items-center gap-0.5 text-[11px] text-emerald-600 hover:text-emerald-700"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {expanded ? 'Show less' : `Show ${entry.bullets.length - 4} more`}
          </button>
        ) : null}
      </div>
    </div>
  );
}

interface Props {
  candidate: Candidate;
  applications: Application[];
}

function StatCard({
  icon: Icon,
  label,
  value,
  accent = false,
}: {
  icon: typeof FileText;
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl border vscode-border vscode-bg-secondary p-4">
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
          accent ? 'bg-emerald-500/10 text-emerald-600' : 'vscode-bg-tertiary vscode-text-tertiary'
        }`}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wider vscode-text-tertiary">{label}</p>
        <p className={`text-[18px] font-semibold ${accent ? 'text-emerald-600' : 'vscode-text-primary'}`}>
          {value}
        </p>
      </div>
    </div>
  );
}

function ContactRow({
  icon: Icon,
  label,
  value,
  href,
  external,
}: {
  icon: typeof Mail;
  label: string;
  value: string;
  href?: string;
  external?: boolean;
}) {
  const content = href ? (
    <a
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      className="text-[13px] text-emerald-600 hover:underline inline-flex items-center gap-1"
    >
      {value}
      {external && <ExternalLink className="h-3 w-3" />}
    </a>
  ) : (
    <span className="text-[13px] vscode-text-primary">{value}</span>
  );

  return (
    <div className="flex items-center gap-3 py-2.5">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg vscode-bg-tertiary">
        <Icon className="h-4 w-4 vscode-text-tertiary" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wider vscode-text-tertiary">{label}</p>
        {content}
      </div>
    </div>
  );
}

export function CandidateOverviewTab({ candidate, applications }: Props) {
  const [copied, setCopied] = useState(false);
  const [showAllSkills, setShowAllSkills] = useState(false);

  const experience = useMemo(() => readExperience(candidate), [candidate]);
  const education: EducationEntry[] = useMemo(() => candidate.education ?? [], [candidate]);
  const skills = useMemo(() => candidate.parsed_skills ?? [], [candidate]);
  const visibleSkills = showAllSkills ? skills : skills.slice(0, 10);

  const linkedinHref = useMemo(() => {
    const li = candidate.linkedin_url?.trim();
    if (!li) return null;
    return li.startsWith('http') ? li : `https://${li}`;
  }, [candidate.linkedin_url]);

  const shareUrl = `${window.location.origin}/candidates/${candidate.id}`;

  const copyShare = () => {
    void navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="space-y-6 pb-8">
      {/* Stats row */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard icon={FileText} label="Applications" value={applications.length} accent />
        <StatCard icon={Award} label="Skills" value={skills.length} />
        <StatCard icon={Clock} label="Years Exp" value={candidate.years_exp ?? '—'} />
        <StatCard icon={Building2} label="Companies" value={experience.length} />
      </div>

      {/* Contact info card */}
      <div className="rounded-xl border vscode-border vscode-bg-secondary p-4">
        <div className="grid grid-cols-1 gap-0 divide-y vscode-border sm:grid-cols-2 sm:divide-x sm:divide-y-0">
          <div className="space-y-0 divide-y vscode-border sm:pr-4">
            <ContactRow
              icon={Mail}
              label="Email"
              value={candidate.email || '—'}
              href={candidate.email ? `mailto:${candidate.email}` : undefined}
            />
            <ContactRow icon={MapPin} label="Location" value={candidate.location || '—'} />
            <ContactRow
              icon={Linkedin}
              label="LinkedIn"
              value={linkedinHref ? 'View profile' : '—'}
              href={linkedinHref || undefined}
              external
            />
          </div>
          <div className="space-y-0 divide-y vscode-border sm:pl-4">
            <ContactRow
              icon={FileText}
              label="Collection"
              value={`${applications.length} application${applications.length !== 1 ? 's' : ''}`}
              href={`/applications?candidateId=${candidate.id}`}
            />
            <ContactRow
              icon={Calendar}
              label="Added"
              value={candidate.created_at ? new Date(candidate.created_at).toLocaleDateString() : '—'}
            />
            <div className="flex items-center gap-3 py-2.5">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg vscode-bg-tertiary">
                <Copy className="h-4 w-4 vscode-text-tertiary" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] uppercase tracking-wider vscode-text-tertiary">Share</p>
                <button
                  type="button"
                  onClick={copyShare}
                  className="inline-flex items-center gap-1 text-[13px] text-emerald-600 hover:text-emerald-700"
                >
                  {copied ? <Check className="h-3.5 w-3.5" /> : null}
                  {copied ? 'Copied!' : 'Copy link'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Skills block - enhanced */}
      {skills.length > 0 ? (
        <div className="rounded-xl border vscode-border vscode-bg-secondary p-4">
          <h3 className="mb-3 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider vscode-text-tertiary">
            <Award className="h-3.5 w-3.5" /> Skills ({skills.length})
          </h3>
          <div className="flex flex-wrap gap-2">
            {visibleSkills.map((s, i) => (
              <span
                key={s}
                className={`rounded-lg px-3 py-1.5 text-[12px] font-medium ${
                  i < 5
                    ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20'
                    : 'border vscode-border vscode-bg-tertiary vscode-text-secondary'
                }`}
              >
                {s}
              </span>
            ))}
          </div>
          {skills.length > 10 ? (
            <button
              type="button"
              className="mt-3 flex items-center gap-1 text-[12px] text-emerald-600 hover:text-emerald-700"
              onClick={() => setShowAllSkills((v) => !v)}
            >
              {showAllSkills ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              {showAllSkills ? 'Show less' : `Show ${skills.length - 10} more skills`}
            </button>
          ) : null}
        </div>
      ) : null}

      {/* Experience - enhanced timeline */}
      {experience.length > 0 ? (
        <div className="rounded-xl border vscode-border vscode-bg-secondary p-4">
          <h3 className="mb-4 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider vscode-text-tertiary">
            <Briefcase className="h-3.5 w-3.5" /> Experience ({experience.length})
          </h3>
          <div className="relative space-y-0">
            {/* Timeline line */}
            <div
              className="absolute left-4 top-4 bottom-4 w-0.5 vscode-bg-tertiary"
              style={{ height: 'calc(100% - 32px)' }}
              aria-hidden
            />
            {experience.map((e, i) => (
              <div key={i} className="relative pl-10 pb-5 last:pb-0">
                {/* Timeline dot */}
                <div
                  className={`absolute left-2.5 top-1 h-3 w-3 rounded-full border-2 ${
                    i === 0
                      ? 'border-emerald-500 bg-emerald-500'
                      : 'border-[var(--vscode-border-primary)] vscode-bg-secondary'
                  }`}
                  aria-hidden
                />
                <ExperienceCard entry={e} />
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Education - enhanced */}
      {education.length > 0 ? (
        <div className="rounded-xl border vscode-border vscode-bg-secondary p-4">
          <h3 className="mb-4 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider vscode-text-tertiary">
            <GraduationCap className="h-3.5 w-3.5" /> Education ({education.length})
          </h3>
          <div className="space-y-3">
            {education.map((edu, i) => (
              <div key={i} className="flex gap-3 rounded-lg border vscode-border vscode-bg-primary p-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-violet-500/10">
                  <GraduationCap className="h-5 w-5 text-violet-600 dark:text-violet-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-[14px] font-semibold vscode-text-primary leading-snug">{edu.degree || '—'}</p>
                  <p className="text-[12px] vscode-text-secondary">
                    {edu.institution || '—'}
                    {edu.year != null ? (
                      <span className="ml-2 rounded-full bg-violet-500/10 px-2 py-0.5 text-[10px] font-medium text-violet-600 dark:text-violet-400">
                        {edu.year}
                      </span>
                    ) : null}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {experience.length === 0 && education.length === 0 && skills.length === 0 && (
        <div className="rounded-xl border vscode-border vscode-bg-secondary p-6 text-center">
          <FileText className="mx-auto h-8 w-8 vscode-text-tertiary mb-2" />
          <p className="text-[13px] vscode-text-tertiary">No profile data available yet.</p>
          <p className="text-[11px] vscode-text-tertiary mt-1">Upload a resume to parse skills and experience.</p>
        </div>
      )}
    </div>
  );
}
